import { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

/**
 * Simple canvas signature pad — mouse + touch.
 * Value is a data URL (image/png) or null.
 */
export default function SignaturePad({ value, onChange, label = "Signature", testid = "signature" }) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const [empty, setEmpty] = useState(!value);

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    // High-DPI setup
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio; canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctxRef.current = ctx;
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value; setEmpty(false);
    }
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current; const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => { e.preventDefault(); drawingRef.current = true; const { x, y } = getPos(e); const c = ctxRef.current; c.beginPath(); c.moveTo(x, y); };
  const move = (e) => { if (!drawingRef.current) return; e.preventDefault(); const { x, y } = getPos(e); const c = ctxRef.current; c.lineTo(x, y); c.stroke(); setEmpty(false); };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const url = canvasRef.current.toDataURL("image/png");
    onChange?.(url);
  };

  const clear = () => {
    const canvas = canvasRef.current; const ctx = ctxRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true); onChange?.(null);
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-semibold tracking-wider text-slate-500 uppercase">{label}</label>
        <button type="button" onClick={clear} data-testid={`${testid}-clear`}
                className="text-xs text-slate-500 hover:text-rose-600 flex items-center gap-1">
          <Eraser className="w-3.5 h-3.5"/> Effacer
        </button>
      </div>
      <div className="relative rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 overflow-hidden" style={{ height: 140 }}>
        <canvas ref={canvasRef} className="w-full h-full touch-none block cursor-crosshair"
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}/>
        {empty && (
          <div className="absolute inset-0 grid place-items-center text-slate-400 text-sm pointer-events-none italic">
            Signez ici (souris ou tactile)
          </div>
        )}
      </div>
    </div>
  );
}
